import { Check, ShieldCheck, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@workspace/ui/components/ui/dialog";
import { getPromiseContent } from "@/content/promise";
import { usePlatform } from "@/hooks/usePlatform";

/** The mobile popup uses the same commitments and limits as the Promise page. */
export function PromiseDetailsDialog() {
  const { t } = useTranslation();
  const platform = usePlatform();
  const content = getPromiseContent(t, platform.name);
  return (
    <DialogContent className="car-promise-dialog flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-lg [&>button]:right-2 [&>button]:top-2 [&>button]:h-11 [&>button]:w-11">
      <DialogHeader className="shrink-0 border-b border-border p-5 pr-12 text-left">
        <DialogTitle className="flex min-w-0 items-start gap-2 text-xl">
          <ShieldCheck className="h-6 w-6 shrink-0 text-brand" aria-hidden="true" />
          <span className="min-w-0 [overflow-wrap:anywhere]">{content.title}</span>
        </DialogTitle>
        <DialogDescription className="pt-2 text-sm leading-6">{content.intro}</DialogDescription>
      </DialogHeader>
      <div className="min-h-0 overflow-y-auto overscroll-contain p-5 text-sm">
        <ul className="space-y-2">
          {content.widgetBenefits.map(benefit => (
            <li key={benefit} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 space-y-5">
          {content.pillars.map(pillar => (
            <section key={pillar.id}>
              <h3 className="font-semibold">{pillar.title}</h3>
              <p className="mt-1 leading-6 text-muted-foreground">
                {pillar.id === "payment" ? content.fundsExplainer : pillar.detail}
              </p>
            </section>
          ))}
          <section>
            <h3 className="font-semibold">{t("promise.covered_heading")}</h3>
            <ul className="mt-2 space-y-2">
              {(["host_cancellation", "material_condition", "platform_payment"] as const).map(key => (
                <li key={key} className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                  <span>{t(`promise.covered.${key}`, { brand: platform.name })}</span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className="font-semibold">{t("promise.excluded_heading")}</h3>
            <ul className="mt-2 space-y-2">
              {(["off_platform", "guest_cancellation", "late_report", "minor_difference"] as const).map(key => (
                <li key={key} className="flex gap-2">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span>{t(`promise.excluded.${key}`, { brand: platform.name })}</span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className="font-semibold">{t("promise.reporting_heading")}</h3>
            <p className="mt-1 leading-6 text-muted-foreground">{content.reportingRule}</p>
          </section>
          <section>
            <h3 className="font-semibold">{t("promise.cancellation_heading")}</h3>
            <p className="mt-1 leading-6 text-muted-foreground">{content.cancellationRule}</p>
          </section>
          <div className="space-y-2 border-t border-border pt-4 text-xs leading-5 text-muted-foreground">
            <p>{content.platformCondition}</p>
            <p>{content.contractualBoundary}</p>
          </div>
        </div>
      </div>
    </DialogContent>
  );
}